import { useEffect, useState, useCallback, useRef } from "react";
import { getRulesList, getRulesListByDomainId } from "../services/rulesService";

interface IuseFetchAllRules {
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
  filters?: {
    rule_name?: string;
    rule_filter_ids?: number | string;
    is_active?: boolean;
    comparison_research_query_available?: boolean;
    is_score_config_associated?: boolean;
  };
  fetchOnMount?: boolean; // Controls whether to fetch data immediately on mount
  currentDomainId?: string | number | null;
}

const useFetchAllRules = ({
  setIsLoading,
  filters,
  fetchOnMount = true, // Default to true for backward compatibility
  currentDomainId,
}: IuseFetchAllRules) => {
  const [allRules, setAllRules] = useState<any[]>([]);
  const [isFetching, setIsFetching] = useState<boolean>(false); // Internal loading state for Autocomplete

  // Use refs for guards and mutable props so the callback identity stays stable,
  // preventing useEffect re-triggers from unrelated re-renders.
  const hasFetchedRef = useRef<boolean>(false);
  const isFetchingRef = useRef<boolean>(false);
  const setIsLoadingRef = useRef(setIsLoading);
  setIsLoadingRef.current = setIsLoading;

  const filtersRef = useRef(filters);
  filtersRef.current = filters;

  const currentDomainIdRef = useRef(currentDomainId);
  currentDomainIdRef.current = currentDomainId;

  const fetchRules = useCallback(async () => {
    // Prevent duplicate fetches AND concurrent in-flight requests
    if (hasFetchedRef.current || isFetchingRef.current) return;

    isFetchingRef.current = true;
    setIsFetching(true);
    setIsLoadingRef.current(true);
    try {
      const domainId = currentDomainIdRef.current;
      let result: any;
      if (domainId && Number(domainId) > 0) {
        // Fetch rules scoped to the selected domain. Forward only the
        // is_score_config_associated filter; other filters are list-wide only.
        result = await getRulesListByDomainId(Number(domainId), {
          is_score_config_associated:
            filtersRef.current?.is_score_config_associated,
        });
      } else {
        result = await getRulesList(filtersRef.current);
      }

      // Ensure result is an array before setting state
      setAllRules(Array.isArray(result) ? result : []);
      hasFetchedRef.current = true;
    } catch (err) {
      console.error("Error fetching rules:", err);
      setAllRules([]); // fallback to empty array
    } finally {
      isFetchingRef.current = false;
      setIsFetching(false);
      setIsLoadingRef.current(false);
    }
  }, []); // No deps — uses refs for all mutable values

  // Reset hasFetched when currentDomainId changes so data is re-fetched for the new domain
  useEffect(() => {
    hasFetchedRef.current = false;
    if (fetchOnMount) {
      fetchRules();
    }
  }, [fetchOnMount, fetchRules, currentDomainId]);

  return [allRules, fetchRules, isFetching] as const;
};

export default useFetchAllRules;
